/* eslint-disable no-lonely-if */
import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { AppThemeProvider } from "../../theme";
import defaultTheme from "../../theme/defaultTheme";
import ChatScreen from "../pages/ChatScreen";
import NotFoundPage from "../pages/NotFoundPage";
import LandingPage from "../pages/LandingPage";
import AppScreen from "../pages/AppScreen";
import ReliefManScreen from "../pages/ReliefManScreen";
import { getLoginAgainUrl } from "../../utils/loginUrl";
import { resolveSessionActive } from "../../utils/sessionProbe";
import AgenticProtectedRoute from "./AgenticProtectedRoute";
import TenantFeatureRoute from "./TenantFeatureRoute";
import { intakeData } from "../../config/agentic/IntakeData";
import { benefitData } from "../../config/agentic/benefitData";
import { eligibilityData } from "../../config/agentic/eligibilityReviewConfig";
import { eligibilityReviewReportConfig } from "../../config/agentic/eligibilityReviewReportConfig";
import { applicationStatusData } from "../../config/agentic/applicationStatusData";
import { applicationStatusConfig } from "../../config/agentic/applicationStatusConfig";
import { docAgentData } from "../../config/agentic/docAgentData";
import { docAgentReportConfig } from "../../config/agentic/docAgentReportConfig";
import { duplicateConfig } from "../../config/agentic/duplicateConfig";
import { benefitDuplicateConfig } from "../../config/agentic/benefitDuplicateConfig";

const AppRoutes = ({ logedInUserMail = '', logedInUserName = '' }) => {
  const [checkedSession, setCheckedSession] = useState(false);
  const [sessionExists, setSessionExists] = useState(false);

  useEffect(() => {
    let cancelled = false;

    // resolveSessionActive() uses the readable `session_exists` cookie when
    // present (vanity domains) and otherwise falls back to a single credentialed
    // BFF probe (default *.azurewebsites.net hosts, where the BFF's cookie is
    // host-only and unreadable from this SPA host — Public Suffix List rule).
    // A false result deterministically routes to login (no retry), so there is
    // no redirect loop when there genuinely is no session.
    resolveSessionActive().then((active) => {
      if (cancelled) return;

      if (!active) {
        window.location.replace(getLoginAgainUrl());
        return;
      }

      setSessionExists(true);
      setCheckedSession(true);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  if (!checkedSession) {
    return null;
  }

  return (
    <Routes>
      <Route path="/" element={<Navigate to="/landing" replace />} />
      <Route path="/landing" element={<LandingPage logedInUserMail={logedInUserMail} />} />
      <Route element={<TenantFeatureRoute flag="showChatButton" />}>
        <Route
          path="/chat/:collaboration_id?"
          element={<ChatScreen logedInUserMail={logedInUserMail} logedInUserName={logedInUserName} />}
        />
      </Route>

      {/* Agentic subtree: session_exists cookie gate already passed above.
          AgenticProtectedRoute re-checks the Track 1 session cookie. The
          agentic Function App is gated by a BFF-minted JWT (see
          utils/agenticApiClient.js) — no second Entra flow. */}
      <Route
        element={
          <AppThemeProvider theme={defaultTheme}>
            <AgenticProtectedRoute />
          </AppThemeProvider>
        }
      >
        <Route element={<TenantFeatureRoute flag="showAppsButton" />}>
          <Route path="/apps" element={<AppScreen logedInUserMail={logedInUserMail} />} />
          <Route path="/intake-agent" element={<ReliefManScreen {...intakeData} logedInUserMail={logedInUserMail} />} />
          <Route path="/benefit-agent" element={<ReliefManScreen {...benefitData} logedInUserMail={logedInUserMail} />} />
          <Route path="/eligibility-review-agent" element={<ReliefManScreen {...eligibilityData} logedInUserMail={logedInUserMail} />} />
          <Route path="/eligibility-review-report" element={<ReliefManScreen {...eligibilityData} {...eligibilityReviewReportConfig} logedInUserMail={logedInUserMail} />} />
          <Route path="/application-status-agent" element={<ReliefManScreen {...applicationStatusData} logedInUserMail={logedInUserMail} />} />
          <Route path="/application-status-report" element={<ReliefManScreen {...applicationStatusConfig} logedInUserMail={logedInUserMail} />} />
          <Route path="/doc-agent" element={<ReliefManScreen {...docAgentData} logedInUserMail={logedInUserMail} />} />
          <Route path="/documentQuality-report" element={<ReliefManScreen {...docAgentData} {...docAgentReportConfig} logedInUserMail={logedInUserMail} />} />
          <Route path="/duplicate-report" element={<ReliefManScreen {...duplicateConfig} logedInUserMail={logedInUserMail} />} />
          <Route path="/benefit-duplicate-report" element={<ReliefManScreen {...benefitDuplicateConfig} logedInUserMail={logedInUserMail} />} />
        </Route>
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

export default AppRoutes;
